import React, { useEffect, useState } from 'react';
import api, { RBAC_BASE } from '../../../api/rbacApi';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../../Redux/slice/features/authSlice";

const RolePermission = () => {
  const { roleId } = useParams();
  const [initialSelected, setInitialSelected] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [showAssigned, setShowAssigned] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userType = useSelector((state) => state.auth.userType);
  const currentUser = useSelector((state) => state.auth.user);


  const location = useLocation();
  const roleName = location.state?.roleName;
  const selectedPerms = permissions.filter(p =>
    selected.includes(p.intPermissionId)
  );

  useEffect(() => {
    if (!roleId) return;
    load();
  }, [roleId]);

  const load = async () => {
    const [allPerms, rolePerms] = await Promise.all([
      api.get(`${RBAC_BASE}/permissions`),
      api.get(`${RBAC_BASE}/roles/${roleId}/permissions`)
    ]);

    // FILTER ONLY ACTIVE
    const activePerms = allPerms.data.filter(p => Boolean(p.bitStatus));
    setPermissions(activePerms);

    // also filter selected (important)
    const validSelected = rolePerms.data.filter(id =>
      activePerms.some(p => p.intPermissionId === id)
    );

    setSelected(validSelected);
    setInitialSelected(validSelected);

    // collapse default
    const collapsedDefault = {};
    activePerms.forEach(p => {
      collapsedDefault[p.vchModule] = true;
    });

    setCollapsed(collapsedDefault);
  };

  const hasChanges =
    selected.length !== initialSelected.length ||
    selected.some(id => !initialSelected.includes(id));

  const toggle = (id) => {
    setSelected(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  // GLOBAL TOGGLE
  const isAllSelected = selected.length === permissions.length;

  const handleGlobalToggle = () => {
    if (isAllSelected) {
      setSelected([]);
    } else {
      setSelected(permissions.map(p => p.intPermissionId));
    }
  };

  // MODULE TOGGLE
  const handleModuleToggle = (modulePerms) => {
    const ids = modulePerms.map(p => p.intPermissionId);
    const allSelected = ids.every(id => selected.includes(id));

    if (allSelected) {
      setSelected(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelected(prev => [...new Set([...prev, ...ids])]);
    }
  };

  // SAVE CHANGES
  const save = async () => {
    if (!hasChanges) return;

    await api.post(`${RBAC_BASE}/roles/assign-permissions`, {
      roleId,
      permissions: selected
    });

    setInitialSelected(selected);

    alert("Permissions updated successfully");

    // CHECK: if current user's role is updated
    const currentRoleId = String(currentUser?.user?.roleId);

    if (currentRoleId === String(roleId)) {

      alert("Your permissions changed. You will be logged out.");

      const isEmployee = sessionStorage.getItem("hrmsLogin");
      const isHrmsPath = window.location.pathname.startsWith("/hrms");

      sessionStorage.removeItem("hrmsLogin");

      await dispatch(logoutUser());

      if (isEmployee || isHrmsPath || userType === "hrms") {
        navigate("/hrms/login", { replace: true });
      } else {
        navigate("/signin", { replace: true });
      }
    }
  };

  // GROUP
  const grouped = permissions.reduce((acc, p) => {
    acc[p.vchModule] = acc[p.vchModule] || [];
    acc[p.vchModule].push(p);
    return acc;
  }, {});

  const handleExpandAll = () => {
    const expanded = {};
    Object.keys(grouped).forEach(module => {
      expanded[module] = false;
    });
    setCollapsed(expanded);
  };

  const handleCollapseAll = () => {
    const collapsedAll = {};
    Object.keys(grouped).forEach(module => {
      collapsedAll[module] = true;
    });
    setCollapsed(collapsedAll);
  };

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">

      <div className="sticky-top bg-white py-2 mb-3 border-bottom d-flex justify-content-between align-items-center">
        <h4 className="fw-bold mb-0">
          Role Permission {roleName ? `- ${roleName}` : ""}
        </h4>

        <div className="d-flex align-items-center gap-3">

          {hasChanges && (
            <span className="text-primary fw-semibold">
              * Unsaved Changes
            </span>
          )}

          <button
            className="btn btn-primary px-4"
            onClick={save}
            disabled={!hasChanges}
          >
            Save Changes
          </button>

        </div>
      </div>

      {/* ASSIGNED PERMISSIONS */}
      {selectedPerms.length >= 0 && (
        <div className="accordion mb-4">

          <div className="accordion-item border-0 shadow-sm">

            {/* HEADER */}
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${!showAssigned ? "collapsed" : ""}`}
                onClick={() => setShowAssigned(prev => !prev)}
                style={{ background: "#f8f9fa", fontWeight: "600" }}
              >
                <div className="d-flex justify-content-between w-100 align-items-center">

                  <span>
                    Assigned Permissions ({selectedPerms.length})
                  </span>

                  <button
                    className="btn btn-sm btn-outline-danger mx-4 px-3"
                    onClick={(e) => {
                      e.stopPropagation(); // 🔥 prevent accordion toggle
                      setSelected([]);
                    }}
                  >
                    Remove All
                  </button>

                </div>
              </button>
            </h2>

            {/* BODY */}
            <div className={`accordion-collapse collapse ${showAssigned ? "show" : ""}`}>
              <div className="accordion-body">

                <div className="row">
                  {selectedPerms.map(p => (
                    <div className="col-md-3 mb-2" key={p.intPermissionId}>

                      <div className="p-2 border rounded d-flex align-items-center bg-light">

                        {/* TEXT */}
                        <div style={{
                          flex: 1,
                          fontSize: "13px",
                          fontWeight: "500",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}>
                          {p.vchCode}
                        </div>

                        {/* BUTTON */}
                        <button
                          className="btn btn-sm btn-outline-danger ms-2 flex-shrink-0"
                          onClick={() => toggle(p.intPermissionId)}
                        >
                          ✕
                        </button>

                      </div>

                    </div>
                  ))}
                </div>

              </div>
            </div>

          </div>

        </div>
      )}

      {/* SEARCH + GLOBAL TOGGLE */}
      <div className="d-flex gap-3 mb-4">
        <input
          className="form-control"
          placeholder="Search permissions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="form-check form-switch d-flex align-items-center gap-2">
          <input
            className="form-check-input"
            type="checkbox"
            checked={isAllSelected}
            onChange={handleGlobalToggle}
          />
          <label className="form-check-label">
            Select All
          </label>
        </div>
      </div>

      <div className="d-flex gap-2 mb-3">
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={handleExpandAll}
        >
          Expand All
        </button>

        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={handleCollapseAll}
        >
          Collapse All
        </button>
      </div>

      {/* MODULES */}
      {Object.keys(grouped).map(module => {
        const modulePerms = grouped[module];

        const filtered = modulePerms.filter(p =>
          p.vchCode.toLowerCase().includes(search.toLowerCase())
        );

        if (!filtered.length) return null;

        const moduleSelected = modulePerms.every(p =>
          selected.includes(p.intPermissionId)
        );

        return (
          <div className="card mb-4 border-0 shadow-sm" key={module}>

            {/* MODULE HEADER */}
            <div className="card-header bg-light d-flex justify-content-between align-items-center">

              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() =>
                    setCollapsed(prev => ({
                      ...prev,
                      [module]: !prev[module]
                    }))
                  }
                >
                  {collapsed[module] ? "+" : "-"}
                </button>

                <h6 className="mb-0 fw-bold">{module}</h6>

                <span className="badge bg-secondary">
                  {modulePerms.length}
                </span>
              </div>

              {/* MODULE TOGGLE SWITCH */}
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={moduleSelected}
                  onChange={() => handleModuleToggle(modulePerms)}
                />
              </div>
            </div>

            {/* MODULE BODY */}
            {!collapsed[module] && (
              <div className="card-body">

                <div className="row">
                  {filtered.map(p => {
                    const isChecked = selected.includes(p.intPermissionId);

                    return (
                      <div className="col-md-4 mb-3" key={p.intPermissionId}>
                        <div
                          className={`p-2 border rounded d-flex justify-content-between align-items-center ${isChecked ? "bg-light border-primary" : ""
                            }`}
                        >

                          {/* TOOLTIP */}
                          <span
                            style={{ fontSize: "13px", cursor: "pointer" }}
                            title={p.vchDescription}
                          >
                            {p.vchCode}
                          </span>

                          {/* TOGGLE */}
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggle(p.intPermissionId)}
                            />
                          </div>

                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            )}
          </div>
        );
      })}

    </div>
  );
};

export default RolePermission;